import { useCallback, useEffect, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import Breadcrumbs from "@mui/material/Breadcrumbs";
import RefreshIcon from "@mui/icons-material/Refresh";
import { useNavigate } from "react-router-dom";

import PipelineService, { pipelineErrorMessage } from "../../services/pipeline.service";
import { IPipelineStatus, IPipelineSummary } from "../../types/api/pipeline.type";
import { ALL_SERVICES, ServiceStateChip, overallState } from "./common";

interface IRowStatus {
  status?: IPipelineStatus;
  error?: string;
}

const Pipelines = () => {
  const navigate = useNavigate();
  const [pipelines, setPipelines] = useState<IPipelineSummary[]>([]);
  const [statuses, setStatuses] = useState<Record<string, IRowStatus>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Bumped on every load, so status responses from an earlier Refresh that
  // arrive late are dropped instead of landing in the new table.
  const loadGeneration = useRef(0);

  const load = useCallback(async () => {
    const generation = ++loadGeneration.current;
    setLoading(true);
    setError(null);
    setStatuses({});
    try {
      const list = await PipelineService.listPipelines();
      if (generation !== loadGeneration.current) return;
      setPipelines(list);
      // Each pipeline's status loads on its own, so one slow or unreachable
      // pipeline doesn't hold up the rest of the table.
      list.forEach(async (p) => {
        let entry: IRowStatus;
        try {
          const { data } = await PipelineService.getStatus(p.envId);
          entry = { status: data };
        } catch (e) {
          entry = { error: pipelineErrorMessage(e, "Unreachable") };
        }
        if (generation === loadGeneration.current) {
          setStatuses((prev) => ({ ...prev, [p.envId]: entry }));
        }
      });
    } catch (e) {
      if (generation !== loadGeneration.current) return;
      setPipelines([]);
      setError(pipelineErrorMessage(e, "Could not list the deployed pipelines"));
    } finally {
      if (generation === loadGeneration.current) setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return (
    <>
      <Breadcrumbs separator="›" aria-label="breadcrumb" sx={{ marginBottom: "20px" }}>
        <Typography color="#777">Pipelines</Typography>
      </Breadcrumbs>
      <Card sx={{ backgroundColor: "#2A2B31", maxWidth: "1450px" }}>
        <CardContent sx={{ paddingBottom: "16px !important" }}>
          <CardHeader
            title="Pipelines"
            subheader="Streaming pipelines deployed in this cluster, one per tenant"
            action={
              <Button startIcon={<RefreshIcon />} onClick={load} disabled={loading}>
                Refresh
              </Button>
            }
          />
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
          {loading ? (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
              <CircularProgress />
            </Box>
          ) : (
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 1050 }} aria-label="pipelines">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: "bold" }}>Tenant</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Environment</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Status</TableCell>
                    <TableCell sx={{ fontWeight: "bold" }}>Services</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {pipelines.length === 0 && !error ? (
                    <TableRow>
                      <TableCell colSpan={4}>No pipelines found!</TableCell>
                    </TableRow>
                  ) : (
                    pipelines.map((p) => {
                      const row = statuses[p.envId];
                      const overall = overallState(row?.status, !!row?.error);
                      const services = row?.status?.services ?? {};
                      return (
                        <TableRow key={p.envId} hover>
                          <TableCell component="th" scope="row">
                            <Link
                              component="h4"
                              sx={{ cursor: "pointer", m: 0 }}
                              onClick={() => navigate(`/Pipelines/${p.envId}`)}
                            >
                              {p.tenantName}
                            </Link>
                          </TableCell>
                          <TableCell>{p.envId}</TableCell>
                          <TableCell>
                            {row ? (
                              <Chip
                                size="small"
                                label={overall.label}
                                color={overall.color}
                                title={row.error}
                              />
                            ) : (
                              <CircularProgress size={16} />
                            )}
                          </TableCell>
                          <TableCell>
                            {row?.status &&
                              ALL_SERVICES.filter((name) => services[name]).map((name) => (
                                <ServiceStateChip key={name} name={name} status={services[name]} />
                              ))}
                            {row?.error && (
                              <Typography variant="body2" color="error">
                                {row.error}
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>
    </>
  );
};

export default Pipelines;
